/* eslint-disable max-lines */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Navigate,
  Path,
  To,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useBuildVars, useSiteConfig } from '../../config';
import {
  Vocabulary,
  Creds,
  Props,
  customTranslationKeys,
  supportedLanguages,
} from './extras';
import OktaSignInWidget from '@okta/okta-signin-widget';
import { useOktaAuth } from '@okta/okta-react';
import { useAuthConfig } from '../auth-context';
import { useLocalizationContext, useTranslate } from '@im/react-localization';
import {
  clearFailedLoginAttempt,
  displayFailedLoginAttempts,
  getUsernameFromLastFailedLoginAttempt,
} from './failed-login-attempts';
import { displayMfaWarning } from './MfaWarningMessage';
import { extractQueryParam } from '../utils';
import { FailedLoginDialog } from './FailedLoginDialog';
import { useSetFailedLoginDialogOpen } from './failed-login-dialog.hooks';
import { useAutoSignIn } from '../useAutoSignIn';
import { useDecodedToken } from '../authorization.hook';
import { useIsAuthorized } from '../useIsAuthorized';
import { NotAuthorizedMessage } from './NotAuthorizedMessage';

declare global {
  interface Window {
    OOo: any;
    isOneTrustEnabled: boolean;
  }
}

let widget: OktaSignInWidget | undefined;
const resellerProspectPath = '/reselleronboarding/resellerdetails';
const DASHBOARD_PATH = '/my/dashboard';
const HOMEPAGE_PATH = '/home';
const DICTIONARY_STORAGE_KEY = 'osw.languages';
const DICTIONARY_TIMESTAMP_STORAGE_KEY = 'osw.languages.timestamp';

export const LoginWidget = (props: Props) => {
  const {
    originalPath: previousPath = '/',
    primaryAuthTitle,
    throwErrors = true,
  } = props;
  const decodedToken = useDecodedToken();
  const translate = useTranslate();
  const resellerProspectUser = decodedToken?.resellerProspect || false;
  const authConfig = useAuthConfig();
  const setFailedLoginDialogOpen = useSetFailedLoginDialogOpen();
  const location = useLocation();
  const navigate = useNavigate();
  const historyState = location.state as { from: Location };
  const { siteCode } = useSiteConfig();
  let originalPath: To = previousPath;
  let originalUsernameParam: undefined | string;
  if (
    historyState?.from?.pathname &&
    !historyState.from.pathname.toLowerCase().includes('/my/checkout')
  ) {
    const { hash, pathname, search } = historyState.from;
    originalPath = { hash, pathname, search };

    //Extract username from query params to prepopulate login form
    const [username, updatedSearch] = extractQueryParam(
      originalPath.search,
      'username'
    );
    originalUsernameParam = username;
    originalPath.search = updatedSearch;
  }

  const { locale, localizationService } = useLocalizationContext();
  const [searchParams] = useSearchParams();
  const widgetRef = useRef<any>();
  const [vocabulary, setVocabulary] = useState<Vocabulary | undefined>();
  const { authState, oktaAuth } = useOktaAuth();
  const isAuthenticated = authState?.isAuthenticated ?? false;
  const isAuthorized = useIsAuthorized();
  const autoSignIn = useAutoSignIn();
  const { appId } = useBuildVars();

  useEffect(() => {
    if (isAuthenticated) {
      return;
    }
    autoSignIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isActive = true;
    localizationService
      .translate(customTranslationKeys)
      .then((res: Vocabulary) => {
        if (isActive) {
          setVocabulary(res);
        }
      });
    return () => {
      isActive = false;
    };
  }, [locale, localizationService]);

  const signInWidgetConfig = useMemo(() => {
    if (!vocabulary) {
      return;
    }

    const { clientId, interactionCodeFlow, issuer, redirectUri } = authConfig;

    let transformedUsername: string;
    const i18n: any = {};
    i18n[locale] = {
      'error.auth.lockedOut': vocabulary['OktaWidget.LockedOut'],
      'error.unsupported.response':
        vocabulary['OktaWidget.UnsupportedResponse'],
      'errors.E0000004': vocabulary['OktaWidget.BadUsernamePassword'],
      'oform.errorbanner.title':
        vocabulary['OktaWidget.AuthFailedContactWebSupport'],
      'primaryauth.submit': vocabulary['OktaWidget.PrimaryAuthSubmit'],
      'primaryauth.title':
        primaryAuthTitle || vocabulary['OktaWidget.PrimaryAuthTitle'],
    };

    const config = {
      assets: {
        baseUrl: '/cep/assets/okta-signin-widget',
        languages: supportedLanguages,
      },
      authClient: oktaAuth,
      authParams: {
        // To avoid redirect do not set "pkce" or "display" here. OKTA-335945
        //issuer,
        //scopes,
      },
      /**
       * Note: when using the Sign-In Widget for an OIDC flow, it still
       * needs to be configured with the base URL for your Okta Org. Here
       * we derive it from the given issuer for convenience.
       */
      baseUrl: issuer.split('/oauth2')[0],
      clientId,

      features: {
        rememberMe: false,
        showPasswordToggleOnSignInPage: true,
      },

      helpLinks: {
        forgotPassword: `/cep/app/frame-public?url=${encodeURIComponent(
          '/Site/Password/Forgot'
        )}`,
      },
      i18n: i18n,
      language: locale,
      processCreds: (creds: Creds, callback: () => void) => {
        const { username } = creds;
        const getTransformUsernamePromise = () =>
          fetch('/Site/Sso/TransformUserNameString', {
            body: username,
            method: 'POST',
          })
            .then((response) => response.text())
            .then((data) => {
              transformedUsername = data;
              return transformedUsername;
            })
            .catch((error) => {
              console.error('username transform error: ', error);
              transformedUsername = username;
              return transformedUsername;
            });

        const getCloseSessionPromise = () =>
          oktaAuth.session.exists().then((sessionExists) => {
            if (sessionExists) {
              return oktaAuth.session.close();
            }
          });

        Promise.all(
          appId === 'cep'
            ? [getTransformUsernamePromise(), getCloseSessionPromise()]
            : [getCloseSessionPromise()]
        )
          .then(() => {
            callback();
          })
          .catch(() => {
            callback();
          });
      },
      recoveryToken: searchParams.get('recovery-token'),
      redirectUri,
      transformUsername: (username: string, _operation: string) => {
        return transformedUsername && transformedUsername.startsWith(username)
          ? transformedUsername
          : username;
      },
      useClassicEngine: !interactionCodeFlow,
      useInteractionCodeFlow: interactionCodeFlow, // Set to true, if your org is OIE enabled
      username:
        (searchParams.get('username') ??
          getUsernameFromLastFailedLoginAttempt()) ||
        originalUsernameParam,
    };

    return config;
  }, [
    appId,
    authConfig,
    locale,
    oktaAuth,
    originalUsernameParam,
    primaryAuthTitle,
    searchParams,
    vocabulary,
  ]);

  useEffect(() => {
    if (!signInWidgetConfig || !vocabulary) {
      return;
    }
    const dictionaryTimestamp = +(
      localStorage.getItem(DICTIONARY_TIMESTAMP_STORAGE_KEY) ?? '0'
    );
    const currentTimestamp = Date.now();
    if (currentTimestamp - dictionaryTimestamp > 86400000) {
      localStorage.removeItem(DICTIONARY_STORAGE_KEY);
      localStorage.setItem(
        DICTIONARY_TIMESTAMP_STORAGE_KEY,
        String(currentTimestamp)
      );
    }
    widget = new OktaSignInWidget(signInWidgetConfig);
    widget.renderEl(
      { el: widgetRef.current },
      (res) => {
        clearFailedLoginAttempt();
        oktaAuth?.handleLoginRedirect(res.tokens);
      },
      (err) => {
        console.error('widget err', err);
        if (throwErrors) {
          throw err;
        }
      }
    );

    widget.on('afterError', (_context, error) => {
      //for some unknown reason authfactor.challenge.soft_token.invalid_passcode
      //always shows up in English and coudn't be overridden in i18n object
      //applying this javascript workaround, support ticket has been sent to Okta
      if (error?.xhr?.responseJSON?.errorCode === 'E0000068') {
        const p = document.querySelector('.okta-form-infobox-error p');
        if (p) {
          p.textContent = vocabulary['OktaWidget.InvalidPasscode'];
        }
      }

      if (error?.xhr?.responseJSON?.errorCode === 'E0000004') {
        displayFailedLoginAttempts(
          true,
          false,
          setFailedLoginDialogOpen,
          vocabulary['OktaWidget.FailedLoginAttempts'],
          vocabulary['OktaWidget.FailedLoginWarning']
        );
      } else if (error?.xhr?.responseJSON?.errorCode === 'E0000119') {
        displayFailedLoginAttempts(
          true,
          true,
          setFailedLoginDialogOpen,
          vocabulary['OktaWidget.FailedLoginAttempts'],
          vocabulary['OktaWidget.FailedLoginWarning']
        );
      }

      const p = document.querySelector<HTMLParagraphElement>(
        '.okta-form-infobox-error.infobox.infobox-error p'
      );
      if (p) {
        p.innerHTML = p.innerText;
      }
    });

    widget.on('afterRender', (_context) => {
      displayMfaWarning({ context: _context, siteCode, translate });

      const ul = document.querySelector('ul#help-links-container');
      ul?.setAttribute('style', 'display: block');

      const a = document.querySelector('a.link.help.js-help');
      a?.setAttribute('style', 'display: none');

      const li = document.querySelector('a.link.js-help-link')?.parentElement;
      li?.setAttribute('style', 'display: none');

      // workaround to naviage to forgot password page using React Router
      const forgotPasswordLink = document.querySelector<HTMLAnchorElement>(
        'a.link.js-forgot-password'
      );

      forgotPasswordLink?.classList.remove('js-forgot-password');
      forgotPasswordLink?.addEventListener('click', (event: any) => {
        event.preventDefault();
        navigate('/forgot-password');
      });

      if (_context.controller === 'primary-auth') {
        displayFailedLoginAttempts(
          false,
          false,
          setFailedLoginDialogOpen,
          vocabulary['OktaWidget.FailedLoginAttempts'],
          vocabulary['OktaWidget.FailedLoginWarning']
        );
      }
    });
    // Return clean-up function
    return () => {
      if (widget) {
        widget.off();
        widget.remove();
        widget = undefined;
      }
    };
  }, [
    appId,
    signInWidgetConfig,
    oktaAuth,
    setFailedLoginDialogOpen,
    vocabulary,
    navigate,
    siteCode,
    throwErrors,
    translate,
  ]);

  if (isAuthenticated && isAuthorized) {
    // No need to show the login form if already authenticated
    if (originalPath?.search?.toString()?.includes('Password')) {
      originalPath.search = '/';
    } else if (Object.values(originalPath)[1]?.includes('password')) {
      originalPath = '/';
    }
    if ((originalPath as Path)?.pathname === HOMEPAGE_PATH) {
      originalPath = DASHBOARD_PATH;
    }
    return (
      <Navigate
        replace
        to={resellerProspectUser ? resellerProspectPath : originalPath}
      />
    );
  }

  return (
    <>
      <div ref={widgetRef} />
      <FailedLoginDialog />
      <NotAuthorizedMessage
        isAuthenticated={isAuthenticated}
        isAuthorized={isAuthorized}
      />
    </>
  );
};
